import React, { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Input, List, Modal, Space, Spin, Tag, Tooltip, Typography, message } from 'antd';
import {
  ArrowUpOutlined,
  FileOutlined,
  FolderOpenOutlined,
  HomeOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../common/security/AuthContext';
import { dccListStats } from '../../services/dccApi';
import FilePreviewModal, { canPreviewFile } from '../common/FilePreviewModal';
import FolderFileSearchModal from './FolderFileSearchModal';

const { Text } = Typography;

export interface SelectDCCFileModalProps {
  open: boolean;
  value?: any;
  options?: any[];
  initialPath?: string;
  mode?: 'single' | 'multiple';
  onCancel: () => void;
  onOk?: (val: any) => void;
  onSelect?: (args: { fullPath: string; name: string }) => void;
  onSelectMultiple?: (args: Array<{ fullPath: string; name: string }>) => void;
}

 interface DccItemRow {
   raw: string;
   name: string;
   isFolder: boolean;
 }

const buildDccFullPath = (basePath: string, name: string) => {
  const b = String(basePath || '').replace(/\\/g, '/').replace(/^\/+/, '').replace(/\/+$/, '');
  const n = String(name || '').replace(/^\/+/, '');
  return b ? `${b}/${n}` : n;
};

const normalizePathInput = (p: string): string => {
  return String(p || '')
    .trim()
    .replace(/^[/\\]+/, '')
    .replace(/^root[\\/]/i, '')
    .replace(/\\/g, '/');
};

const parseMultiValue = (value: unknown): Array<{ fullPath: string; name: string }> => {
  const rawList = Array.isArray(value)
    ? value
    : String(value ?? '')
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean);
  return rawList.map((entry) => {
    const fullPath = String(entry).replace(/\\/g, '/');
    const parts = fullPath.split('/').filter(Boolean);
    const name = parts.length ? parts[parts.length - 1] : fullPath;
    return { fullPath, name };
  });
};

const SelectDCCFileModal: React.FC<SelectDCCFileModalProps> = ({
  open,
  value,
  options,
  initialPath = '',
  mode = 'single',
  onCancel,
  onOk,
  onSelect,
  onSelectMultiple,
}) => {
  const { t } = useTranslation();
  const { token } = useAuth();

  const [listPath, setListPath] = useState<string>(initialPath);
  const [nameFilter, setNameFilter] = useState<string>('');
  const [items, setItems] = useState<DccItemRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<{ fullPath: string; name: string } | null>(null);
  const [selectedItems, setSelectedItems] = useState<Array<{ fullPath: string; name: string }>>([]);

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewPath, setPreviewPath] = useState<string | null>(null);

  const [isFolderSearchOpen, setIsFolderSearchOpen] = useState(false);

  const breadcrumbSegments = useMemo(() => {
    return String(listPath || '')
      .split('/')
      .map((s) => s.trim())
      .filter(Boolean);
  }, [listPath]);

  const initialShortcut = useMemo(() => normalizePathInput(initialPath), [initialPath]);

  const selectedIsFolder = useMemo(() => {
    if (!selected) return false;
    const rawName = selected.name.replace(/\/$/, '');
    const hasExtension = /\.[^./\\]+$/.test(rawName);
    return selected.name.endsWith('/') || !hasExtension;
  }, [selected]);

  const selectedKeySet = useMemo(() => {
    const set = new Set<string>();
    (selectedItems || []).forEach((s) => set.add(s.fullPath));
    return set;
  }, [selectedItems]);

  // Files only — folders cannot be confirmed in multiple mode
  const multiFilePicks = useMemo(() => {
    return (selectedItems || []).filter((s) => {
      const rawName = String(s.name || '').replace(/\/$/, '');
      const hasExtension = /\.[^./\\]+$/.test(rawName);
      const isFolder = String(s.name || '').endsWith('/') || !hasExtension;
      return !isFolder;
    });
  }, [selectedItems]);

  const canConfirm =
    mode === 'multiple' ? multiFilePicks.length > 0 : !!selected && !selectedIsFolder;

  const mapDccStatsItems = (data: any[]): DccItemRow[] => {
    return (data || []).map((item) => {
      const raw = String(item?.name ?? '');
      const rawName = raw.replace(/\/$/, '');
      const isFolder = Boolean(item?.is_dir);
      return { raw, name: rawName, isFolder };
    });
  };

  const fetchItems = async (nextPath: string) => {
    const normalizedNext = normalizePathInput(nextPath);
    if (normalizePathInput(listPath) !== normalizedNext) {
      setNameFilter('');
    }
    if (!token) {
      message.error(t('dccFileModal.notAuthenticated'));
      return;
    }
    setLoading(true);
    try {
      const data = await dccListStats(normalizePathInput(nextPath), token);
      setItems(Array.isArray(data) ? mapDccStatsItems(data) : []);
      setListPath(normalizedNext);
      setSelected(null);
    } catch (e: any) {
      message.error(e?.message || t('dccFileModal.loadError'));
      setItems([]);
      setSelected(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setListPath(initialPath);
    setNameFilter('');
    setSelected(null);
    setSelectedItems(mode === 'multiple' ? parseMultiValue(value) : []);
    setIsPreviewOpen(false);
    setPreviewPath(null);
    fetchItems(initialPath);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialPath]);

  useEffect(() => {
    // If opened via GenericFormFill, it may pass `value` instead of using onSelect.
    // Keep selection initialization minimal: do not attempt to navigate folders here.
    if (!open) return;
    if (mode === 'single') {
      const v = value != null ? String(value) : '';
      if (!v) return;
      const parts = v.replace(/\\/g, '/').split('/').filter(Boolean);
      const name = parts.length ? parts[parts.length - 1] : v;
      setSelected({ fullPath: v, name });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const emitSingle = (args: { fullPath: string; name: string }) => {
    onSelect?.(args);
    onOk?.(args.fullPath);
  };

  const emitMultiple = (args: Array<{ fullPath: string; name: string }>) => {
    onSelectMultiple?.(args);
    if (!onSelectMultiple && args.length > 0) onSelect?.(args[0]);
    onOk?.(args.map((x) => x.fullPath));
  };

  const handleGoUp = async () => {
    if (!listPath) return;
    const parts = listPath.split('/').filter(Boolean);
    parts.pop();
    const next = parts.join('/');
    await fetchItems(next);
  };

  const handleConfirm = () => {
    if (mode === 'multiple') {
      if (multiFilePicks.length === 0) return;
      emitMultiple(multiFilePicks);
      onCancel();
      return;
    }
    if (!selected || selectedIsFolder) return;
    emitSingle({ fullPath: selected.fullPath, name: selected.name });
    onCancel();
  };

  const handleSelectAndClose = (args: { fullPath: string; name: string }) => {
    if (mode === 'multiple') {
      // In multiple mode, do not auto-close on double click; rely on Select button
      return;
    }
    emitSingle(args);
    onCancel();
  };

  const toggleMultiSelection = (args: { fullPath: string; name: string }) => {
    setSelectedItems((prev) => {
      const p = prev || [];
      const idx = p.findIndex((x) => x.fullPath === args.fullPath);
      if (idx >= 0) {
        return p.filter((x) => x.fullPath !== args.fullPath);
      }
      return p.concat(args);
    });
  };

  const visibleFiles = useMemo(() => {
    const normalizedFilter = String(nameFilter || '').trim().toLowerCase();
    return items.filter((row) => {
      if (row.isFolder) return false;
      if (normalizedFilter && !row.name.toLowerCase().includes(normalizedFilter)) return false;
      return true;
    });
  }, [items, nameFilter]);

  const allVisibleChecked =
    visibleFiles.length > 0 &&
    visibleFiles.every((row) => selectedKeySet.has(buildDccFullPath(listPath, row.name)));
  const someVisibleChecked = visibleFiles.some((row) =>
    selectedKeySet.has(buildDccFullPath(listPath, row.name)),
  );

  const toggleAllVisible = (checked: boolean) => {
    setSelectedItems((prev) => {
      const map = new Map((prev || []).map((s) => [s.fullPath, s]));
      visibleFiles.forEach((row) => {
        const fullPath = buildDccFullPath(listPath, row.name);
        if (checked) map.set(fullPath, { fullPath, name: row.name });
        else map.delete(fullPath);
      });
      return Array.from(map.values());
    });
  };

  const renderItemRow = (row: DccItemRow, basePath: string, depth: number) => {
    const fullPath = buildDccFullPath(basePath, row.name);
    const isSelected = selected?.fullPath === fullPath;
    const normalizedFilter = String(nameFilter || '').trim().toLowerCase();
    const matchesSelf = !normalizedFilter || row.name.toLowerCase().includes(normalizedFilter);
    const previewable = !row.isFolder && canPreviewFile(row.name);
    const isMulti = mode === 'multiple';
    const isChecked = isMulti && selectedKeySet.has(fullPath);

    if (normalizedFilter && !matchesSelf) return null;

    return (
      <div key={`${basePath}::${row.raw}`}>
        <List.Item
          style={{
            cursor: 'pointer',
            background: isSelected || isChecked ? '#e6f7ff' : undefined,
            userSelect: 'none',
            paddingLeft: 12 + depth * 16,
          }}
          onClick={() => {
            if (row.isFolder) {
              if (isMulti) fetchItems(fullPath);
              else setSelected({ fullPath, name: row.name });
              return;
            }
            if (isMulti) {
              toggleMultiSelection({ fullPath, name: row.name });
              return;
            }
            setSelected({ fullPath, name: row.name });
          }}
          onDoubleClick={() => {
            if (row.isFolder) {
              fetchItems(fullPath);
              return;
            }
            if (isMulti) return;
            handleSelectAndClose({ fullPath, name: row.name });
          }}
          actions={
            row.isFolder
              ? []
              : previewable
                ? [
                    <Button
                      key="preview"
                      size="small"
                      type="link"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPreviewPath(fullPath);
                        setIsPreviewOpen(true);
                      }}
                    >
                      {t('dccFileModal.preview')}
                    </Button>,
                  ]
                : []
          }
        >
          <Space size="small">
            {isMulti ? (
              <Checkbox
                checked={isChecked}
                disabled={row.isFolder}
                onClick={(e) => e.stopPropagation()}
                onChange={() => {
                  if (!row.isFolder) toggleMultiSelection({ fullPath, name: row.name });
                }}
              />
            ) : null}
            {row.isFolder ? <FolderOpenOutlined /> : <FileOutlined />}
            <Text>{row.name}</Text>
          </Space>
        </List.Item>
      </div>
    );
  };

  return (
    <>
      <Modal
        title={mode === 'multiple' ? t('dccFileModal.titleMultiple') : t('dccFileModal.title')}
        open={open}
        onCancel={onCancel}
        width={720}
        destroyOnClose
        footer={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <Text
              type="secondary"
              style={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textAlign: 'left',
              }}
            >
              {mode === 'multiple'
                ? t('dccFileModal.selectedCount', { total: multiFilePicks.length })
                : selected && !selectedIsFolder
                  ? t('dccFileModal.selectedName', { name: selected.name })
                  : t('dccFileModal.doubleClickHint')}
            </Text>
            <Space>
              <Button onClick={onCancel}>{t('dccFileModal.cancel')}</Button>
              <Button type="primary" onClick={handleConfirm} disabled={loading || !canConfirm}>
                {t('dccFileModal.select')}
              </Button>
            </Space>
          </div>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <Space size={4} style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <Text type="secondary">{t('dccFileModal.path')}</Text>
              {breadcrumbSegments.length === 0 ? (
                <Text type="secondary">/</Text>
              ) : (
                breadcrumbSegments.map((seg, idx) => {
                  const p = breadcrumbSegments.slice(0, idx + 1).join('/');
                  return (
                    <Space key={`${p}-${idx}`} size={4}>
                      <Button
                        size="small"
                        type="link"
                        onClick={() => fetchItems(p)}
                        disabled={loading}
                        title={seg}
                        style={{ padding: 0, height: 'auto', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }}
                      >
                        {seg}
                      </Button>
                      {idx < breadcrumbSegments.length - 1 ? <Text type="secondary">/</Text> : null}
                    </Space>
                  );
                })
              )}
            </Space>
            <Space size={4}>
              <Tooltip title={t('dccFileModal.searchFolder')}>
                <Button
                  size="small"
                  icon={<SearchOutlined />}
                  onClick={() => setIsFolderSearchOpen(true)}
                  disabled={loading}
                />
              </Tooltip>
              {initialShortcut ? (
                <Tooltip title={t('dccFileModal.goToInitial')}>
                  <Button
                    size="small"
                    icon={<HomeOutlined />}
                    onClick={() => fetchItems(initialShortcut)}
                    disabled={loading}
                  />
                </Tooltip>
              ) : null}
              <Tooltip title={t('dccFileModal.upOneLevel')}>
                <Button
                  size="small"
                  icon={<ArrowUpOutlined />}
                  onClick={handleGoUp}
                  disabled={!listPath || loading}
                />
              </Tooltip>
              <Tooltip title={t('dccFileModal.refresh')}>
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={() => fetchItems(listPath)}
                  disabled={loading}
                />
              </Tooltip>
            </Space>
          </div>
          <Input
            allowClear
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            placeholder={t('dccFileModal.filterPlaceholder')}
          />
          {mode === 'multiple' ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <Checkbox
                checked={allVisibleChecked}
                indeterminate={someVisibleChecked && !allVisibleChecked}
                disabled={loading || visibleFiles.length === 0}
                onChange={(e) => toggleAllVisible(e.target.checked)}
              >
                {t('dccFileModal.selectAll')}
              </Checkbox>
              <Button
                size="small"
                type="link"
                disabled={multiFilePicks.length === 0}
                onClick={() => setSelectedItems([])}
              >
                {t('dccFileModal.clearSelection')}
              </Button>
            </div>
          ) : null}
          {mode === 'multiple' && multiFilePicks.length > 0 ? (
            <div style={{ maxHeight: 72, overflowY: 'auto' }}>
              <Space size={[4, 4]} wrap>
                {multiFilePicks.map((item) => (
                  <Tag
                    key={item.fullPath}
                    closable
                    onClose={(e) => {
                      e.preventDefault();
                      toggleMultiSelection(item);
                    }}
                  >
                    {item.name}
                  </Tag>
                ))}
              </Space>
            </div>
          ) : null}
          <Spin spinning={loading}>
            <div style={{ maxHeight: '48vh', overflowY: 'auto' }}>
              <List
                size="small"
                bordered
                dataSource={items}
                locale={{ emptyText: t('dccFileModal.empty') }}
                renderItem={(row) => renderItemRow(row, listPath, 0) as any}
              />
            </div>
          </Spin>
        </Space>
      </Modal>

      <FolderFileSearchModal
        open={isFolderSearchOpen}
        rootPath={listPath}
        returnOption="only_folder"
        onOk={(val: any) => {
          const p = val != null ? String(val).trim() : '';
          if (p) fetchItems(p);
          setIsFolderSearchOpen(false);
        }}
        onCancel={() => setIsFolderSearchOpen(false)}
      />

      <FilePreviewModal
        open={isPreviewOpen}
        path={previewPath}
        token={token}
        onClose={() => {
          setIsPreviewOpen(false);
          setPreviewPath(null);
        }}
      />
    </>
  );
};

export default SelectDCCFileModal;
